"use client";

/**
 * Public terms of service body (/terms) — bilingual.
 *
 * Rendered inside {@link EnglishMarketingScope}, so it DEFAULTS to English and
 * the EN/NL toggle in {@link LandingNav} flips the locale to the Dutch source.
 * The full bilingual copy lives locally in {@link COPY} (not the shared i18n
 * dictionary): a legal base version is easiest to keep faithful when its Dutch
 * source and the English translation sit side by side.
 *
 * Scope (deliberate): this is a public B2B/SaaS BASE version. It does not claim
 * bespoke legal advice, does not promise uptime/SLA, and explicitly defers
 * pilots, enterprise terms, SLAs and the DPA/verwerkersovereenkomst to separate
 * written agreements. All legal identity (KvK / BTW / address / inbox) comes
 * from the central {@link COMPANY} config so nothing is duplicated here.
 */

import type { ReactNode } from "react";
import Link from "next/link";
import { LandingNav } from "@/components/landing-nav";
import { COMPANY } from "@/lib/company";
import { PrintTermsButton } from "@/components/terms/print-button";
import { useLocale } from "@/i18n/provider";

const LAST_UPDATED = "2026-06-23";

const COPY = {
  nl: {
    eyebrow: "Algemene voorwaarden · FixControl",
    title: "Algemene voorwaarden",
    intro:
      "Deze voorwaarden gelden voor het gebruik van FixControl, tenzij schriftelijk anders overeengekomen.",
    lastUpdated: "Laatst bijgewerkt",
    disclaimer:
      "Deze algemene voorwaarden vormen een publieke basisversie. Bij pilots, enterprise-afspraken of maatwerk kunnen aanvullende of afwijkende afspraken worden vastgelegd in een offerte, order form, SLA, verwerkersovereenkomst of andere schriftelijke overeenkomst.",
    s1Title: "Definities",
    s1Intro: "In deze voorwaarden betekenen de volgende begrippen:",
    s1Defs: [
      { term: "FixControl", body: " — de dienst en het platform dat onder de handelsnaam FixControl wordt aangeboden." },
      { term: COMPANY.legalName, body: " — de besloten vennootschap die FixControl als handelsnaam voert en de dienst levert; in deze voorwaarden ook “wij”, “ons” of “de leverancier”." },
      { term: "Klant", body: " — de rechtspersoon of onderneming die een overeenkomst aangaat of de dienst gebruikt." },
      { term: "Gebruiker", body: " — een natuurlijke persoon die namens de klant toegang heeft tot de dienst." },
      { term: "Dienst", body: " — het FixControl-platform en de bijbehorende functionaliteit, integraties en ondersteuning." },
      { term: "Account", body: " — de toegang waarmee de klant en zijn gebruikers de dienst gebruiken." },
      { term: "Tenant / project / workspace", body: " — de logisch afgescheiden omgeving(en) waarin klantdata, configuratie en workflows van de klant zijn ondergebracht." },
      { term: "Integraties", body: " — koppelingen met systemen van derden, zoals Slack, Freshdesk, Jira, Linear, GitHub, GitLab, Kubernetes/Argo en AI-diensten, afhankelijk van configuratie." },
      { term: "AI-functionaliteit", body: " — onderdelen van de dienst die met behulp van AI-modellen triage, samenvattingen, planvorming, conceptantwoorden, remediation-voorstellen en vergelijkbare ondersteuning leveren." },
      { term: "Klantdata", body: " — alle gegevens die de klant of zijn gebruikers in of via de dienst verwerken, waaronder data uit gekoppelde systemen." },
      { term: "Vertrouwelijke informatie", body: " — niet-publieke informatie die een partij van de andere ontvangt en redelijkerwijs als vertrouwelijk moet worden beschouwd." },
      { term: "Overeenkomst", body: ` — het geheel van afspraken tussen de klant en ${COMPANY.legalName} over het gebruik van de dienst, inclusief deze voorwaarden.` },
    ],
    s2Title: "Toepasselijkheid",
    s2P1: "Deze voorwaarden gelden voor alle offertes, pilots, accounts, toegang tot de dienst en gebruik van FixControl. Afwijkingen gelden alleen voor zover deze schriftelijk zijn overeengekomen.",
    s2P2: "Bij strijdigheid tussen documenten geldt de volgende rangorde:",
    s2List: [
      "een aparte schriftelijke overeenkomst of order form;",
      "de verwerkersovereenkomst/DPA voor de verwerking van persoonsgegevens;",
      "een SLA of supportafspraken, indien overeengekomen;",
      "deze algemene voorwaarden.",
    ],
    s2P3: "Inkoop- of andere algemene voorwaarden van de klant zijn uitdrukkelijk uitgesloten, tenzij wij die schriftelijk hebben aanvaard.",
    s3Title: "Totstandkoming van de overeenkomst",
    s3Paras: [
      "Een overeenkomst komt tot stand door schriftelijke acceptatie, ondertekening, akkoord op een offerte of order form, of door feitelijk gebruik van de dienst nadat toegang is verstrekt.",
      "Pilots en proefopstellingen kunnen een eigen scope, duur en beperkingen hebben. Demo’s en previews scheppen geen verplichting om alle getoonde functionaliteit te leveren.",
    ],
    s4Title: "Toegang, accounts en gebruikers",
    s4Paras: [
      "De klant is verantwoordelijk voor het juist inrichten van gebruikers, rollen en toegangsrechten. Gebruikers moeten hun inloggegevens geheim houden en zorgvuldig met toegang omgaan.",
      "De klant meldt misbruik of ongeautoriseerde toegang zo snel mogelijk. Wij mogen toegang beperken of opschorten bij misbruik, een beveiligingsrisico of overtreding van deze voorwaarden.",
    ],
    s5Title: "Gebruik van de dienst",
    s5P1: "Binnen de afgesproken scope is onder meer toegestaan:",
    s5Allowed: [
      "support-, engineering-, deployment- en governance-workflows beheren;",
      "integraties configureren binnen de eigen rechten;",
      "AI-ondersteunde voorstellen, reviews en workflows gebruiken binnen de afgesproken scope.",
    ],
    s5P2: "Niet toegestaan is in elk geval:",
    s5Forbidden: [
      "illegale activiteiten;",
      "misbruik van integraties of gekoppelde systemen;",
      "het omzeilen van beveiliging of governance-maatregelen;",
      "het uploaden of verspreiden van malware;",
      "ongeautoriseerde toegang tot systemen of data van anderen;",
      "gebruik in strijd met de wet of met rechten van derden.",
    ],
    s6Title: "AI-functionaliteit en menselijke beoordeling",
    s6Paras: [
      "FixControl bevat AI-functionaliteit die workflows ondersteunt, zoals triage, samenvatting, planvorming, conceptantwoorden, remediation-voorstellen en vergelijkbare taken.",
      "AI-output kan onjuist, onvolledig of ongeschikt zijn. De klant blijft verantwoordelijk voor de beoordeling, goedkeuring en het gebruik van die output. Risicovolle acties, productie-acties, klantcommunicatie en code- of deploymentbeslissingen moeten volgens de ingerichte governance en approval gates worden beoordeeld door bevoegde personen.",
      "FixControl is geen vervanging voor het professionele oordeel van engineering, security, legal, compliance of operations. Welke AI-diensten en modellen worden ingezet, kan afhankelijk zijn van configuratie, klantafspraken en beschikbaarheid. Wij garanderen niet dat AI-output altijd juist of veilig is.",
    ],
    s7Title: "Integraties met derden",
    s7Paras: [
      "FixControl kan, afhankelijk van configuratie, koppelen met systemen van derden. De klant is verantwoordelijk voor het rechtmatig gebruik van gekoppelde accounts, API-keys en rechten.",
      "De beschikbaarheid en werking van systemen van derden vallen buiten onze volledige controle. Wijzigingen of storingen bij derde partijen kunnen invloed hebben op de functionaliteit. Wij verwerken secrets en tokens beveiligd, maar de klant houdt rechten minimaal (least privilege) en volgt een passend rotatiebeleid.",
    ],
    s8Title: "Klantdata en privacy",
    s8P1: "De klant behoudt de rechten op zijn klantdata. Wij verwerken klantdata om de dienst te leveren, te beveiligen en te ondersteunen.",
    s8P2Before: "Persoonsgegevens worden verwerkt volgens onze ",
    s8PrivacyLink: "privacyverklaring",
    s8P2After:
      " en, waar van toepassing, een verwerkersovereenkomst/DPA. De klant is verantwoordelijk voor het rechtmatig aanleveren van data, waaronder data uit Slack, ticketsystemen, repositories of deploymentplatforms.",
    s8P3: "Wij verkopen geen klantdata. Over subverwerkers en eventuele inzet van data voor modeltraining gelden geen verdergaande toezeggingen dan wat elders contractueel of in de privacyverklaring is geregeld.",
    s9Title: "Vertrouwelijkheid",
    s9Paras: [
      "Beide partijen behandelen vertrouwelijke informatie zorgvuldig en gebruiken die alleen voor de uitvoering van de overeenkomst.",
      "De geheimhoudingsplicht geldt niet voor informatie die publiek is, al rechtmatig bekend was, zelfstandig is ontwikkeld, of die op grond van de wet of een bevoegde autoriteit moet worden gedeeld. De geheimhouding blijft gelden na beëindiging van de overeenkomst.",
    ],
    s10Title: "Beveiliging",
    s10Paras: [
      "Wij nemen passende technische en organisatorische maatregelen om de dienst en klantdata te beschermen. Voorbeelden zijn toegangscontrole, logging en audit trails, tenant- en projectscheiding, secret management, versleuteling waar passend, en monitoring.",
      "Volledige (100%) veiligheid kan niet worden gegarandeerd. De klant is zelf verantwoordelijk voor zijn eigen securityconfiguratie, gebruikersrechten en de beveiliging van gekoppelde systemen.",
    ],
    s11Title: "Beschikbaarheid, onderhoud en support",
    s11Paras: [
      "Wij streven naar een betrouwbare beschikbaarheid van de dienst. Onderhoud, updates en incidenten kunnen de beschikbaarheid tijdelijk beïnvloeden.",
      "Supportkanalen en responstijden gelden volgens aparte afspraken, tenzij anders vermeld. Deze voorwaarden bevatten geen harde uptime-garantie of SLA; een SLA geldt alleen als die afzonderlijk is overeengekomen.",
    ],
    s12Title: "Vergoedingen en betaling",
    s12Paras: [
      "Vergoedingen staan in de offerte, order form of overeenkomst. Facturen worden binnen de afgesproken termijn betaald. Prijzen zijn exclusief btw, tenzij anders vermeld.",
      "Bij te late betaling kunnen wij de toegang opschorten na een redelijke waarschuwing, tenzij de wet anders bepaalt. Pilotprijzen en proefperiodes gelden alleen voor de afgesproken scope en duur.",
    ],
    s13Title: "Intellectueel eigendom",
    s13Paras: [
      `FixControl, de software, documentatie, ontwerpen, modellen en configuraties en de bijbehorende knowhow blijven eigendom van ${COMPANY.legalName} of haar licentiegevers. De klant krijgt een beperkt, niet-exclusief en niet-overdraagbaar gebruiksrecht voor de duur van de overeenkomst.`,
      "Klantdata blijft van de klant. Feedback van de klant mogen wij gebruiken om de dienst te verbeteren, zonder daarbij vertrouwelijke klantinformatie openbaar te maken.",
      "Voor AI-output geldt, voor zover wettelijk mogelijk, dat de klant deze mag gebruiken voor zijn eigen bedrijfsprocessen. Wij garanderen niet onbeperkt de juistheid van output of dat deze geen rechten van derden raakt. Output kan lijken op output voor andere klanten waar het om generieke patronen gaat.",
    ],
    s14Title: "Acceptatie, wijzigingen en deployments",
    s14Paras: [
      "Goedkeuringen via Slack, FixControl of gekoppelde kanalen kunnen binnen de ingerichte workflow gelden als operationele goedkeuring. De klant is verantwoordelijk voor wie goedkeuringsrechten krijgt.",
      "Een goedkeuring kan leiden tot vervolgacties, zoals het starten van een missie, het uitvoeren van een retry, het versturen van een conceptantwoord of het promoten van een deployment, afhankelijk van de configuratie. Wij leggen waar passend audit events vast. De klant blijft verantwoordelijk voor de productie-impact van eigen goedkeuringen en van gekoppelde systemen.",
    ],
    s15Title: "Aansprakelijkheid",
    s15Paras: [
      "Voor zover wettelijk toegestaan zijn wij niet aansprakelijk voor indirecte schade, zoals winstderving, omzetverlies, gemiste besparingen, reputatieschade, dataverlies of gevolgschade.",
      "Onze totale aansprakelijkheid is beperkt tot het bedrag dat de klant in de twaalf maanden voorafgaand aan de schadeveroorzakende gebeurtenis voor de betreffende dienst heeft betaald, of tot een redelijk maximum als er nog niets is betaald.",
      `Deze beperkingen gelden niet bij opzet of bewuste roekeloosheid van ${COMPANY.legalName}, en evenmin voor zover een beperking wettelijk niet is toegestaan.`,
    ],
    s16Title: "Vrijwaring",
    s16Paras: [
      `De klant vrijwaart ${COMPANY.legalName} voor aanspraken van derden die voortkomen uit onrechtmatige klantdata, misbruik van accounts of integraties, overtreding van de wet, of gebruik van de dienst buiten de afgesproken scope. Deze vrijwaring blijft redelijk en in verhouding tot de betreffende aanspraak.`,
    ],
    s17Title: "Duur en beëindiging",
    s17Paras: [
      "De duur volgt uit de overeenkomst, order form of pilotafspraak. Opzegging gebeurt volgens de afgesproken termijn.",
      "Wij mogen de dienst opschorten of de overeenkomst beëindigen bij een ernstige overtreding, misbruik, een beveiligingsrisico of wanbetaling, na een redelijke waarschuwing. Na beëindiging kan de toegang worden afgesloten. Export of verwijdering van data verloopt volgens de overeenkomst, de privacyverklaring of de DPA.",
    ],
    s18Title: "Wijzigingen van de voorwaarden",
    s18P1Before: "Wij kunnen deze voorwaarden wijzigen. Een nieuwe versie wordt gepubliceerd op ",
    s18LinkText: "fixcontrol.ai/terms",
    s18P1After: ". Bij materiële wijzigingen kunnen wij de klant actief informeren.",
    s18P2: "Voor lopende schriftelijke overeenkomsten gelden wijzigingen volgens de contractafspraken of na een redelijke aankondiging.",
    s19Title: "Overmacht",
    s19Paras: [
      "Wij zijn niet aansprakelijk voor tekortkomingen die het gevolg zijn van omstandigheden buiten onze redelijke controle, zoals storingen bij cloudproviders, internetstoringen, oorlog, overheidsmaatregelen, stakingen, cyberaanvallen of storingen bij integraties van derden.",
    ],
    s20Title: "Toepasselijk recht en bevoegde rechter",
    s20Paras: [
      "Op deze voorwaarden en de overeenkomst is Nederlands recht van toepassing. Partijen proberen geschillen eerst in onderling overleg op te lossen.",
      "Lukt dat niet, dan worden geschillen voorgelegd aan de bevoegde rechter in Nederland, bij voorkeur de rechtbank Noord-Holland, tenzij dwingend recht een andere rechter aanwijst.",
    ],
    s21Title: "Contact",
    tradeNameLine: `een handelsnaam van ${COMPANY.legalName}`,
    kvkLabel: "KvK",
    vatLabel: "BTW-id",
    emailLabel: "E-mail",
  },
  en: {
    eyebrow: "Terms of Service · FixControl",
    title: "Terms of Service",
    intro:
      "These terms apply to the use of FixControl, unless otherwise agreed in writing.",
    lastUpdated: "Last updated",
    disclaimer:
      "These terms of service are a public base version. For pilots, enterprise arrangements or custom work, additional or differing terms may be recorded in a quote, order form, SLA, data processing agreement (DPA) or other written agreement.",
    s1Title: "Definitions",
    s1Intro: "In these terms, the following terms have the following meaning:",
    s1Defs: [
      { term: "FixControl", body: " — the service and platform offered under the FixControl trade name." },
      { term: COMPANY.legalName, body: " — the private limited company that operates FixControl as a trade name and provides the service; in these terms also referred to as “we”, “us” or “the supplier”." },
      { term: "Customer", body: " — the legal entity or business that enters into an agreement or uses the service." },
      { term: "User", body: " — a natural person who has access to the service on behalf of the customer." },
      { term: "Service", body: " — the FixControl platform and its associated functionality, integrations and support." },
      { term: "Account", body: " — the access through which the customer and its users use the service." },
      { term: "Tenant / project / workspace", body: " — the logically separated environment(s) in which the customer’s data, configuration and workflows are housed." },
      { term: "Integrations", body: " — connections with third-party systems, such as Slack, Freshdesk, Jira, Linear, GitHub, GitLab, Kubernetes/Argo and AI services, depending on configuration." },
      { term: "AI functionality", body: " — components of the service that, using AI models, provide triage, summaries, planning, draft replies, remediation proposals and similar support." },
      { term: "Customer data", body: " — all data that the customer or its users process in or through the service, including data from connected systems." },
      { term: "Confidential information", body: " — non-public information that one party receives from the other and that should reasonably be regarded as confidential." },
      { term: "Agreement", body: ` — the entirety of the arrangements between the customer and ${COMPANY.legalName} regarding the use of the service, including these terms.` },
    ],
    s2Title: "Applicability",
    s2P1: "These terms apply to all quotes, pilots, accounts, access to the service and use of FixControl. Deviations apply only to the extent agreed in writing.",
    s2P2: "In the event of a conflict between documents, the following order of precedence applies:",
    s2List: [
      "a separate written agreement or order form;",
      "the data processing agreement (DPA) for the processing of personal data;",
      "an SLA or support arrangements, if agreed;",
      "these terms of service.",
    ],
    s2P3: "The customer’s purchasing or other general terms are expressly excluded, unless we have accepted them in writing.",
    s3Title: "Formation of the agreement",
    s3Paras: [
      "An agreement is formed by written acceptance, signature, agreement to a quote or order form, or by actual use of the service after access has been granted.",
      "Pilots and trial set-ups may have their own scope, duration and limitations. Demos and previews create no obligation to deliver all the functionality shown.",
    ],
    s4Title: "Access, accounts and users",
    s4Paras: [
      "The customer is responsible for correctly configuring users, roles and access rights. Users must keep their login credentials confidential and handle access with care.",
      "The customer reports misuse or unauthorised access as soon as possible. We may restrict or suspend access in the event of misuse, a security risk or a breach of these terms.",
    ],
    s5Title: "Use of the service",
    s5P1: "Within the agreed scope, the following is permitted, among other things:",
    s5Allowed: [
      "managing support, engineering, deployment and governance workflows;",
      "configuring integrations within one’s own permissions;",
      "using AI-assisted proposals, reviews and workflows within the agreed scope.",
    ],
    s5P2: "The following is in any case not permitted:",
    s5Forbidden: [
      "illegal activities;",
      "misuse of integrations or connected systems;",
      "circumventing security or governance measures;",
      "uploading or distributing malware;",
      "unauthorised access to the systems or data of others;",
      "use in breach of the law or of the rights of third parties.",
    ],
    s6Title: "AI functionality and human review",
    s6Paras: [
      "FixControl includes AI functionality that supports workflows, such as triage, summarisation, planning, draft replies, remediation proposals and similar tasks.",
      "AI output may be incorrect, incomplete or unsuitable. The customer remains responsible for the review, approval and use of that output. High-risk actions, production actions, customer communications and code or deployment decisions must be reviewed by authorised persons in accordance with the configured governance and approval gates.",
      "FixControl is not a substitute for the professional judgement of engineering, security, legal, compliance or operations. Which AI services and models are used may depend on configuration, customer arrangements and availability. We do not guarantee that AI output is always correct or safe.",
    ],
    s7Title: "Third-party integrations",
    s7Paras: [
      "FixControl can, depending on configuration, connect with third-party systems. The customer is responsible for the lawful use of connected accounts, API keys and permissions.",
      "The availability and operation of third-party systems are outside our full control. Changes or outages at third parties may affect functionality. We process secrets and tokens securely, but the customer keeps permissions minimal (least privilege) and follows an appropriate rotation policy.",
    ],
    s8Title: "Customer data and privacy",
    s8P1: "The customer retains the rights to its customer data. We process customer data to deliver, secure and support the service.",
    s8P2Before: "Personal data is processed in accordance with our ",
    s8PrivacyLink: "privacy policy",
    s8P2After:
      " and, where applicable, a data processing agreement (DPA). The customer is responsible for the lawful provision of data, including data from Slack, ticketing systems, repositories or deployment platforms.",
    s8P3: "We do not sell customer data. Regarding sub-processors and any use of data for model training, no further commitments apply than those laid down elsewhere contractually or in the privacy policy.",
    s9Title: "Confidentiality",
    s9Paras: [
      "Both parties handle confidential information with care and use it only for the performance of the agreement.",
      "The duty of confidentiality does not apply to information that is public, was already lawfully known, was independently developed, or that must be disclosed by law or by a competent authority. The confidentiality obligation continues to apply after termination of the agreement.",
    ],
    s10Title: "Security",
    s10Paras: [
      "We take appropriate technical and organisational measures to protect the service and customer data. Examples are access control, logging and audit trails, tenant and project separation, secret management, encryption where appropriate, and monitoring.",
      "Complete (100%) security cannot be guaranteed. The customer is itself responsible for its own security configuration, user permissions and the security of connected systems.",
    ],
    s11Title: "Availability, maintenance and support",
    s11Paras: [
      "We strive for reliable availability of the service. Maintenance, updates and incidents may temporarily affect availability.",
      "Support channels and response times apply in accordance with separate arrangements, unless stated otherwise. These terms contain no hard uptime guarantee or SLA; an SLA applies only if it has been separately agreed.",
    ],
    s12Title: "Fees and payment",
    s12Paras: [
      "Fees are set out in the quote, order form or agreement. Invoices are paid within the agreed term. Prices are exclusive of VAT, unless stated otherwise.",
      "In the event of late payment, we may suspend access after a reasonable warning, unless the law provides otherwise. Pilot prices and trial periods apply only to the agreed scope and duration.",
    ],
    s13Title: "Intellectual property",
    s13Paras: [
      `FixControl, the software, documentation, designs, models and configurations and the associated know-how remain the property of ${COMPANY.legalName} or its licensors. The customer receives a limited, non-exclusive and non-transferable right of use for the duration of the agreement.`,
      "Customer data remains the customer’s. We may use customer feedback to improve the service, without disclosing confidential customer information.",
      "For AI output, to the extent legally possible, the customer may use it for its own business processes. We do not guarantee without limitation the accuracy of output or that it does not affect the rights of third parties. Output may resemble output for other customers where it concerns generic patterns.",
    ],
    s14Title: "Acceptance, changes and deployments",
    s14Paras: [
      "Approvals via Slack, FixControl or connected channels may, within the configured workflow, constitute operational approval. The customer is responsible for who is granted approval rights.",
      "An approval may lead to follow-up actions, such as starting a mission, executing a retry, sending a draft reply or promoting a deployment, depending on the configuration. We record audit events where appropriate. The customer remains responsible for the production impact of its own approvals and of connected systems.",
    ],
    s15Title: "Liability",
    s15Paras: [
      "To the extent permitted by law, we are not liable for indirect damage, such as loss of profit, loss of revenue, missed savings, reputational damage, data loss or consequential damage.",
      "Our total liability is limited to the amount the customer paid for the relevant service in the twelve months preceding the event causing the damage, or to a reasonable maximum if nothing has yet been paid.",
      `These limitations do not apply in the event of intent or wilful recklessness by ${COMPANY.legalName}, nor to the extent that a limitation is not permitted by law.`,
    ],
    s16Title: "Indemnification",
    s16Paras: [
      `The customer indemnifies ${COMPANY.legalName} against third-party claims arising from unlawful customer data, misuse of accounts or integrations, breach of the law, or use of the service outside the agreed scope. This indemnification remains reasonable and proportionate to the relevant claim.`,
    ],
    s17Title: "Term and termination",
    s17Paras: [
      "The term follows from the agreement, order form or pilot arrangement. Termination takes place in accordance with the agreed notice period.",
      "We may suspend the service or terminate the agreement in the event of a serious breach, misuse, a security risk or non-payment, after a reasonable warning. After termination, access may be closed off. Export or deletion of data takes place in accordance with the agreement, the privacy policy or the DPA.",
    ],
    s18Title: "Changes to the terms",
    s18P1Before: "We may amend these terms. A new version is published at ",
    s18LinkText: "fixcontrol.ai/terms",
    s18P1After: ". In the event of material changes, we may actively inform the customer.",
    s18P2: "For ongoing written agreements, changes apply in accordance with the contractual arrangements or after a reasonable announcement.",
    s19Title: "Force majeure",
    s19Paras: [
      "We are not liable for shortcomings resulting from circumstances beyond our reasonable control, such as outages at cloud providers, internet outages, war, government measures, strikes, cyberattacks or outages at third-party integrations.",
    ],
    s20Title: "Governing law and competent court",
    s20Paras: [
      "These terms and the agreement are governed by Dutch law. The parties first try to resolve disputes by mutual consultation.",
      "If that does not succeed, disputes are submitted to the competent court in the Netherlands, preferably the District Court of Noord-Holland, unless mandatory law designates a different court.",
    ],
    s21Title: "Contact",
    tradeNameLine: `a trade name of ${COMPANY.legalName}`,
    kvkLabel: "KvK",
    vatLabel: "VAT ID",
    emailLabel: "Email",
  },
} as const;

function Section({
  n,
  id,
  title,
  children,
}: {
  n: number;
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-[18px] font-semibold tracking-tight text-fg-bright">
        <span className="text-fg-muted tabular-nums">{n}.</span> {title}
      </h2>
      <div className="mt-3 space-y-3 text-[14px] leading-relaxed text-fg-dim">
        {children}
      </div>
    </section>
  );
}

export function TermsArticle() {
  const { locale } = useLocale();
  const c = locale === "nl" ? COPY.nl : COPY.en;
  const { address } = COMPANY;

  return (
    <div className="min-h-screen bg-bg-base text-fg">
      <div className="print:hidden">
        <LandingNav />
      </div>

      <main className="mx-auto max-w-3xl px-6 pt-16 pb-24">
        {/* Header */}
        <header>
          <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
            {c.eyebrow}
          </p>
          <h1 className="mt-4 text-balance text-4xl font-semibold tracking-tight text-fg-bright leading-[1.1]">
            {c.title}
          </h1>
          <p className="mt-4 text-[15px] text-fg-dim leading-relaxed">
            {c.intro}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <p className="text-[13px] text-fg-muted">
              {c.lastUpdated}: <span className="text-fg-dim">{LAST_UPDATED}</span>
            </p>
            <PrintTermsButton />
          </div>

          {/* Base-version disclaimer */}
          <div className="mt-8 rounded-xl border border-line-subtle bg-bg-raised px-5 py-4 text-[13px] leading-relaxed text-fg-dim">
            {c.disclaimer}
          </div>
        </header>

        {/* Body */}
        <div className="mt-12 space-y-10">
          <Section n={1} id="definities" title={c.s1Title}>
            <p>{c.s1Intro}</p>
            <ul className="space-y-2 list-none">
              {c.s1Defs.map((d) => (
                <li key={d.term}>
                  <strong className="text-fg">{d.term}</strong>
                  {d.body}
                </li>
              ))}
            </ul>
          </Section>

          <Section n={2} id="toepasselijkheid" title={c.s2Title}>
            <p>{c.s2P1}</p>
            <p>{c.s2P2}</p>
            <ol className="ml-5 list-decimal space-y-1">
              {c.s2List.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
            <p>{c.s2P3}</p>
          </Section>

          <Section n={3} id="totstandkoming" title={c.s3Title}>
            {c.s3Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={4} id="toegang" title={c.s4Title}>
            {c.s4Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={5} id="gebruik" title={c.s5Title}>
            <p>{c.s5P1}</p>
            <ul className="ml-5 list-disc space-y-1">
              {c.s5Allowed.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>{c.s5P2}</p>
            <ul className="ml-5 list-disc space-y-1">
              {c.s5Forbidden.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section n={6} id="ai" title={c.s6Title}>
            {c.s6Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={7} id="integraties" title={c.s7Title}>
            {c.s7Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={8} id="klantdata" title={c.s8Title}>
            <p>{c.s8P1}</p>
            <p>
              {c.s8P2Before}
              <Link
                href="/privacy"
                className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
              >
                {c.s8PrivacyLink}
              </Link>
              {c.s8P2After}
            </p>
            <p>{c.s8P3}</p>
          </Section>

          <Section n={9} id="vertrouwelijkheid" title={c.s9Title}>
            {c.s9Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={10} id="beveiliging" title={c.s10Title}>
            {c.s10Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={11} id="beschikbaarheid" title={c.s11Title}>
            {c.s11Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={12} id="vergoedingen" title={c.s12Title}>
            {c.s12Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={13} id="ip" title={c.s13Title}>
            {c.s13Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={14} id="acceptatie" title={c.s14Title}>
            {c.s14Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={15} id="aansprakelijkheid" title={c.s15Title}>
            {c.s15Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={16} id="vrijwaring" title={c.s16Title}>
            {c.s16Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={17} id="duur" title={c.s17Title}>
            {c.s17Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={18} id="wijzigingen" title={c.s18Title}>
            <p>
              {c.s18P1Before}
              <Link
                href="/terms"
                className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
              >
                {c.s18LinkText}
              </Link>
              {c.s18P1After}
            </p>
            <p>{c.s18P2}</p>
          </Section>

          <Section n={19} id="overmacht" title={c.s19Title}>
            {c.s19Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={20} id="recht" title={c.s20Title}>
            {c.s20Paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section n={21} id="contact" title={c.s21Title}>
            <div className="rounded-xl border border-line-subtle bg-bg-raised px-5 py-4">
              <address className="not-italic leading-relaxed text-[14px] text-fg-dim">
                <span className="text-fg">FixControl</span>
                <br />
                {c.tradeNameLine}
                <br />
                {address.street}
                <br />
                {address.postalCode} {address.city}
                <br />
                {address.country}
                <br />
                <br />
                {COMPANY.chamberOfCommerceNumber ? (
                  <>
                    {c.kvkLabel}: {COMPANY.chamberOfCommerceNumber}
                    <br />
                  </>
                ) : null}
                {COMPANY.vatId ? (
                  <>
                    {c.vatLabel}: {COMPANY.vatId}
                    <br />
                  </>
                ) : null}
                {c.emailLabel}:{" "}
                <a
                  href={`mailto:${COMPANY.publicEmail}`}
                  className="text-brand-300 hover:text-brand-200"
                >
                  {COMPANY.publicEmail}
                </a>
              </address>
            </div>
          </Section>
        </div>
      </main>
    </div>
  );
}
